import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { apiRequest, ApiError, errorMessage } from '../lib/api'
import { buildAiRequestContext } from '../lib/aiContext'
import { useAuth } from '../state/AuthContext'
import type { AiIntent, AssistantContext } from '../types'
import { loadSearchIndex, searchRecords } from '../lib/search'
import { COURSE_VERSION } from '../version'

interface AiCitation {
  courseId: string
  moduleId: string
  sectionId: string
  title: string
  url: string
}

interface AiResult {
  answer: string
  citations: AiCitation[]
  limitations?: string[]
  suggestedActions?: string[]
}

interface StartedRequest {
  requestId: string
  conversationId: string
  status: 'pending' | 'running' | 'succeeded' | 'failed'
  pollAfterMs?: number
  result?: AiResult | null
  error?: {
    code: 'AI_TIMEOUT' | 'AI_FAILED'
    message: string
    retryable: true
  } | null
}

interface ChatMessage {
  id: string
  scope?: string
  links?: Array<{ url: string; label: string }>
  role: 'user' | 'assistant'
  text: string
  result?: AiResult
}

type AuthMode = 'signin' | 'signup' | 'signup-code' | 'forgot' | 'reset'

export function AuthPanel() {
  const auth = useAuth()
  const [mode, setMode] = useState<AuthMode>('signin')
  const [email, setEmail] = useState('')
  const [passcode, setPasscode] = useState('')
  const [confirmPasscode, setConfirmPasscode] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  function switchMode(next: AuthMode) {
    setMode(next)
    setError('')
    setNotice('')
    setCode('')
    if (next === 'signin') {
      setPasscode('')
      setConfirmPasscode('')
    }
  }

  function validatePasscode() {
    if (passcode.length < 8) throw new Error('Use at least 8 characters for the passcode.')
    if (passcode !== confirmPasscode) throw new Error('The two passcodes do not match.')
  }

  async function run(task: () => Promise<void>) {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await task()
    } catch (reason) {
      setError(errorMessage(reason))
    } finally {
      setBusy(false)
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    await run(async () => {
      const normalEmail = email.trim().toLowerCase()
      if (!/^\S+@\S+\.\S+$/.test(normalEmail)) throw new Error('Enter a valid email address.')
      if (mode === 'signin') {
        if (!passcode) throw new Error('Enter your passcode.')
        await auth.signIn(normalEmail, passcode)
      } else if (mode === 'signup') {
        validatePasscode()
        await auth.createAccountStart(normalEmail, passcode)
        setMode('signup-code')
        setNotice('A six-digit verification code was sent. It expires in 10 minutes.')
      } else if (mode === 'signup-code') {
        if (code.length !== 6) throw new Error('Enter the six-digit code.')
        await auth.createAccountVerify(normalEmail, code)
      } else if (mode === 'forgot') {
        await auth.forgotPasscodeStart(normalEmail)
        setMode('reset')
        setNotice('If the account is eligible, a six-digit reset code was sent. It expires in 10 minutes.')
      } else if (mode === 'reset') {
        validatePasscode()
        if (code.length !== 6) throw new Error('Enter the six-digit reset code.')
        await auth.resetPasscode(normalEmail, code, passcode)
        switchMode('signin')
        setNotice('Passcode reset. Sign in with the new passcode.')
      }
    })
  }

  const needsNewPasscode = mode === 'signup' || mode === 'reset'
  const needsCode = mode === 'signup-code' || mode === 'reset'
  const submitLabel = {
    signin: 'Sign in and continue',
    signup: 'Send verification code',
    'signup-code': 'Verify and create account',
    forgot: 'Send reset code',
    reset: 'Reset passcode',
  }[mode]

  return (
    <div className="auth-panel">
      <div className="notice info">
        <strong>The course does not require an account.</strong>
        <span> Sign in only to use AI and synchronize progress across devices.</span>
      </div>
      <div className="segmented" role="tablist" aria-label="Account action">
        <button type="button" className={mode === 'signin' ? 'active' : ''} onClick={() => switchMode('signin')}>Sign in</button>
        <button type="button" className={mode.startsWith('signup') ? 'active' : ''} onClick={() => switchMode('signup')}>New account</button>
      </div>
      <form className="form-stack" onSubmit={(event) => void submit(event)}>
        <label><span>Email address</span><input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        {mode === 'signin' ? (
          <label><span>Passcode</span><input type="password" autoComplete="current-password" value={passcode} onChange={(event) => setPasscode(event.target.value)} /></label>
        ) : null}
        {needsNewPasscode ? (
          <>
            <label><span>{mode === 'signup' ? 'Passcode' : 'New passcode'}</span><input type="password" autoComplete="new-password" value={passcode} onChange={(event) => setPasscode(event.target.value)} /></label>
            <label><span>Confirm passcode</span><input type="password" autoComplete="new-password" value={confirmPasscode} onChange={(event) => setConfirmPasscode(event.target.value)} /></label>
          </>
        ) : null}
        {needsCode ? (
          <label><span>Six-digit email code</span><input className="code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))} /></label>
        ) : null}
        <button className="button primary wide" type="submit" disabled={busy}>{busy ? 'Working…' : submitLabel}</button>
      </form>
      {mode === 'signin' ? <button className="text-button" type="button" onClick={() => switchMode('forgot')}>Forgot your passcode?</button> : null}
      {mode !== 'signin' ? <button className="text-button" type="button" onClick={() => switchMode('signin')}>Back to sign in</button> : null}
      {notice ? <div className="notice success" role="status">{notice}</div> : null}
      {error ? <div className="notice error" role="alert">{error}</div> : null}
    </div>
  )
}

export function AccountSettings() {
  const auth = useAuth()
  const [open, setOpen] = useState(false)
  const [currentPasscode, setCurrentPasscode] = useState('')
  const [newPasscode, setNewPasscode] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  async function change(event: FormEvent) {
    event.preventDefault()
    setError('')
    setMessage('')
    if (newPasscode.length < 8) return setError('Use at least 8 characters for the new passcode.')
    if (newPasscode !== confirm) return setError('The two new passcodes do not match.')
    setBusy(true)
    try {
      const remains = await auth.changePasscode(currentPasscode, newPasscode)
      setMessage(remains ? 'Passcode changed.' : 'Passcode changed. Sign in again with the new passcode.')
      setCurrentPasscode('')
      setNewPasscode('')
      setConfirm('')
    } catch (reason) {
      setError(errorMessage(reason))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="account-tools">
      <button className="text-button" type="button" onClick={() => setOpen((value) => !value)}>{open ? 'Close account settings' : 'Account settings'}</button>
      {open ? (
        <form className="form-stack compact" onSubmit={(event) => void change(event)}>
          <label><span>Current passcode</span><input type="password" autoComplete="current-password" value={currentPasscode} onChange={(event) => setCurrentPasscode(event.target.value)} /></label>
          <label><span>New passcode</span><input type="password" autoComplete="new-password" value={newPasscode} onChange={(event) => setNewPasscode(event.target.value)} /></label>
          <label><span>Confirm new passcode</span><input type="password" autoComplete="new-password" value={confirm} onChange={(event) => setConfirm(event.target.value)} /></label>
          <button className="button" type="submit" disabled={busy}>Change passcode</button>
          <button className="button quiet" type="button" disabled={busy} onClick={() => void auth.signOut()}>Sign out</button>
          {message ? <div className="notice success">{message}</div> : null}
          {error ? <div className="notice error">{error}</div> : null}
        </form>
      ) : null}
    </div>
  )
}

async function pollRequest(
  requestId: string,
  token: string,
  initialDelay = 1_500,
  onRemaining?: (remaining: number) => void,
): Promise<{ result: AiResult; remainingToday?: number }> {
  const startedAt = Date.now()
  let delay = Math.min(5_000, Math.max(500, initialDelay))
  while (Date.now() - startedAt < 180_000) {
    await new Promise((resolve) => window.setTimeout(resolve, delay))
    const response = await apiRequest<StartedRequest>('get_ai_request', { requestId }, { token })
    if (typeof response.meta.ai?.remainingToday === 'number') onRemaining?.(response.meta.ai.remainingToday)
    if (response.data.status === 'succeeded' && response.data.result) {
      return { result: response.data.result, remainingToday: response.meta.ai?.remainingToday }
    }
    if (response.data.status === 'failed') {
      const failure = response.data.error
      throw new ApiError({
        code: failure?.code ?? 'AI_FAILED',
        message: failure?.message ?? 'The AI request could not be completed.',
        retryable: failure?.retryable ?? true,
      })
    }
    delay = Math.min(5_000, Math.max(500, response.data.pollAfterMs ?? delay))
  }
  throw new ApiError({
    code: 'AI_TIMEOUT',
    message: 'The AI request is still processing. You can safely try checking it again without starting duplicate work.',
    retryable: true,
  })
}

interface AiAssistantProps {
  context?: AssistantContext
  expanded?: boolean
  onExpandedChange?: (value: boolean) => void
  starterRequest?: number
  starterPrompt?: string
  initialIntent?: AiIntent
  initialQuery?: string
}

const sectionActions: Array<{ intent: AiIntent; label: string; question: string }> = [
  { intent: 'explain', label: 'Explain simply', question: 'Explain this in simpler language with one practical example.' },
  { intent: 'compare', label: 'Compare approaches', question: 'Compare the main approaches and when each may fit.' },
  { intent: 'hint', label: 'Give a hint', question: 'Give me a hint that helps me reason through this without revealing the answer.' },
  { intent: 'next_step', label: 'What next?', question: 'What related topic should I learn next, and why?' },
]

const generalActions: typeof sectionActions = [
  { intent: 'explain', label: 'Explain a concept', question: 'What does diversification mean for a beginner investor?' },
  { intent: 'compare', label: 'Compare approaches', question: 'Compare stocks and bonds for a long-term investor.' },
  { intent: 'next_step', label: 'Where do I start?', question: 'Where should a beginner start learning about investing?' },
]

export function AiAssistant({ context, expanded = true, onExpandedChange, starterRequest = 0, starterPrompt, initialIntent, initialQuery }: AiAssistantProps) {
  const { account, session } = useAuth()
  const location = useLocation()
  const [scope, setScope] = useState<'general' | 'section'>(context?.sectionId ? 'section' : 'general')
  const [intent, setIntent] = useState<AiIntent>(initialIntent ?? 'explain')
  const [query, setQuery] = useState(initialQuery ?? '')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [conversationId, setConversationId] = useState<string | null>(null)
  const [remainingToday, setRemainingToday] = useState<number | null>(null)
  const [height, setHeight] = useState(530)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const formRef = useRef<HTMLTextAreaElement>(null)
  const lastAttemptRef = useRef<{ fingerprint: string; key: string } | null>(null)
  const sectionReady = Boolean(context?.sectionId && context.sectionText?.trim())
  const sectionLabel = [context?.moduleId, context?.sectionLabel].filter(Boolean).join(' · ')
  const returnParams = new URLSearchParams(location.search)
  if (context?.sectionId) returnParams.set('ai', '1')
  const returnPath = `${location.pathname}${returnParams.size ? `?${returnParams.toString()}` : ''}${location.hash}`
  const signInUrl = `/account?returnTo=${encodeURIComponent(returnPath)}`

  useEffect(() => {
    if (!starterRequest) return
    setScope('section')
    setIntent('explain')
    setQuery(starterPrompt ?? sectionActions[0].question)
    formRef.current?.focus()
  }, [starterRequest, starterPrompt])

  useEffect(() => {
    if (initialQuery === undefined) return
    setQuery(initialQuery)
    setIntent(initialIntent ?? 'explain')
  }, [initialQuery, initialIntent])

  function chooseAction(action: (typeof sectionActions)[number]) {
    setIntent(action.intent)
    setQuery(action.question)
    formRef.current?.focus()
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!session || !query.trim() || busy || (scope === 'section' && !sectionReady)) return
    const cleanQuery = query.trim()
    const currentContext = scope === 'section' ? context ?? {} : {}
    const requestContext = buildAiRequestContext(currentContext, currentContext.sectionText, COURSE_VERSION)
    const fingerprint = JSON.stringify({ intent, query: cleanQuery, requestContext, conversationId })
    const previous = lastAttemptRef.current
    const retrying = previous?.fingerprint === fingerprint
    const idempotencyKey = retrying ? previous.key : crypto.randomUUID()
    lastAttemptRef.current = { fingerprint, key: idempotencyKey }
    if (!retrying) setMessages((current) => [...current, {
      id: crypto.randomUUID(), role: 'user', text: cleanQuery,
      scope: scope === 'section' ? sectionLabel : 'General question',
    }])
    setQuery('')
    setError('')
    setBusy(true)
    try {
      const started = await apiRequest<StartedRequest>('start_ai_request', {
        idempotencyKey, conversationId, intent, query: cleanQuery, context: requestContext,
      }, { token: session.token })
      if (typeof started.meta.ai?.remainingToday === 'number') setRemainingToday(started.meta.ai.remainingToday)
      if (started.data.status === 'failed') {
        throw new ApiError({
          code: started.data.error?.code ?? 'AI_FAILED',
          message: started.data.error?.message ?? 'The AI request could not be completed.',
          retryable: started.data.error?.retryable ?? true,
        })
      }
      if (started.data.status !== 'succeeded' && !started.data.requestId) {
        throw new Error('The AI service did not return a request to check.')
      }
      const completed = started.data.status === 'succeeded' && started.data.result
        ? { result: started.data.result, remainingToday: started.meta.ai?.remainingToday }
        : await pollRequest(started.data.requestId, session.token, started.data.pollAfterMs, setRemainingToday)
      setConversationId(started.data.conversationId)
      if (typeof completed.remainingToday === 'number') setRemainingToday(completed.remainingToday)
      let links: Array<{ url: string; label: string }> = []
      if (scope === 'section' && context?.moduleId && context.sectionId) {
        links = [{ url: `/modules/${context.moduleId}#${context.sectionId}`, label: `${context.moduleId} · ${context.sectionLabel ?? 'Current section'}` }]
      } else {
        try {
          const records = await loadSearchIndex()
          links = searchRecords(records, cleanQuery, 3).map((item) => ({ url: item.url, label: `${item.moduleId} · ${item.sectionLabel}` }))
        } catch {
          // The answer remains readable when the local search index is unavailable.
        }
      }
      setMessages((current) => [...current, {
        id: crypto.randomUUID(), role: 'assistant', text: completed.result.answer,
        result: completed.result, links,
      }])
      lastAttemptRef.current = null
    } catch (reason) {
      setError(errorMessage(reason))
      setQuery(cleanQuery)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section id="ai-help" className={`ai-help${expanded ? ' expanded' : ''}`} aria-label="AI learning help">
      <div className="ai-help-heading">
        <div><span className="eyebrow">Optional learning help</span><h2>{context?.moduleId ? `Ask about ${context.moduleId}` : 'Ask AI about investing'}</h2></div>
        {onExpandedChange ? <button className="button" type="button" onClick={() => onExpandedChange(!expanded)} aria-expanded={expanded}>{expanded ? 'Hide AI help' : 'Open AI help'}</button> : null}
      </div>
      {expanded ? (
        <div className={`ai-help-workspace${!session || !account ? ' signed-out' : ''}`} style={{ height }}>
          <div className="ai-help-toolbar">
            <div className="ai-scope-switch" role="group" aria-label="Question scope">
              <button type="button" className={scope === 'general' ? 'active' : ''} onClick={() => { if (scope !== 'general') { setConversationId(null); setScope('general'); setQuery(''); setIntent('explain') } }} aria-pressed={scope === 'general'}>General question</button>
              {context?.sectionId ? <button type="button" className={scope === 'section' ? 'active' : ''} onClick={() => { if (scope !== 'section') { setConversationId(null); setScope('section'); setQuery(''); setIntent('explain') } }} aria-pressed={scope === 'section'}>Current section</button> : null}
            </div>
            <div className="ai-size-controls" aria-label="AI help size">
              <button type="button" onClick={() => setHeight((value) => Math.max(360, value - 120))} disabled={height <= 360} aria-label="Make AI help shorter">−</button>
              <span>Resize</span>
              <button type="button" onClick={() => setHeight((value) => Math.min(850, value + 120))} disabled={height >= 850} aria-label="Make AI help taller">+</button>
            </div>
          </div>
          {!session || !account ? <div className="notice info ai-signin-note">Sign in to ask AI. The course remains available without an account. <Link className="button primary" to={signInUrl}>Sign in or create account</Link></div> : null}
          <div className="ai-help-scroll">
            <p className="ai-context-note">{scope === 'section' ? `Using ${sectionLabel || 'the current section'}${sectionReady ? '' : ' · loading section text…'}` : 'Ask any investing question. No lesson context is sent.'}</p>
            <div className="assistant-warning"><strong>AI can be wrong.</strong> Verify important financial, tax, legal, and benefit information. The course content and calculators remain authoritative.</div>
            <div className="ai-quick-actions" aria-label="Suggested AI questions">
              {(scope === 'section' ? sectionActions : generalActions).map((action) => <button className="button" type="button" key={action.intent} onClick={() => chooseAction(action)}>{action.label}</button>)}
            </div>
            <div className="ai-help-messages" aria-live="polite">
              {!messages.length ? <p className="ai-empty-state">Ask a question in your own words, or choose a suggestion above. The AI runs only when you press Send.</p> : null}
              {messages.map((message) => <article key={message.id} className={`chat-message ${message.role}`}>
                <span>{message.role === 'user' ? `You · ${message.scope}` : 'AI answer'}</span>
                <p>{message.text}</p>
                {message.role === 'assistant' && message.links?.length ? <div className="ai-course-links"><strong>Course sections in this app</strong>{message.links.map((item) => <Link key={item.url} to={item.url}>{item.label}</Link>)}</div> : null}
              </article>)}
              {busy ? <p role="status">Working on your question…</p> : null}
            </div>
          </div>
          <form className="ai-help-composer" onSubmit={(event) => void submit(event)}>
            <label htmlFor={context?.moduleId ? `ai-question-${context.moduleId}` : 'ai-question-general'}>Your question</label>
            <textarea ref={formRef} id={context?.moduleId ? `ai-question-${context.moduleId}` : 'ai-question-general'} rows={3} value={query} onChange={(event) => { setQuery(event.target.value); lastAttemptRef.current = null }} placeholder={scope === 'section' ? 'Ask about this section…' : 'Ask an investing question…'} />
            <div className="ai-composer-bottom"><span className="small muted">{remainingToday === null ? 'Nothing is sent until you press Send.' : `${remainingToday} of 20 AI questions remaining today.`}</span><button className="button primary" type="submit" disabled={!session || busy || !query.trim() || (scope === 'section' && !sectionReady)}>{busy ? 'Working…' : 'Send question'}</button></div>
            {error ? <div className="notice error" role="alert">{error}{/session|sign.in|unauthorized/i.test(error) ? <Link to={signInUrl}> Sign in again</Link> : null}</div> : null}
          </form>
          <p className="ai-resize-hint">Drag the lower corner to resize on desktop, or use the Resize buttons.</p>
        </div>
      ) : null}
    </section>
  )
}
